// AMDM template C2 — the column contract for an imported stock-movement file,
// given by AMDM on 2026-09-03 and recorded in
// docs/analiza-alerte-agregari-amdm-2026-09-03.md. Caiet UC50-11 / UC50-12.
//
// NO BACKEND IMPORT ENDPOINT EXISTS. There is no bulk or file route anywhere
// under apps/bff/src/stock or apps/core-service/src/stock — the only stock
// writes are the draft trio (POST /stock/drafts, PATCH /stock/drafts/:id,
// POST /stock/drafts/:id/confirm) and the machine surface on public-api. So
// this module parses and checks the file in the browser and feeds the rows
// through the draft, where core-service re-runs its own validation. What a
// dedicated endpoint would need is written at the end of this file.
//
// The two rules the file cannot be read without are NOT reimplemented here:
// they are read from the same shared module core-service reads
// (libs/shared/types/src/lib/stock.ts), so the screen cannot drift from
// apps/core-service/src/stock/draft.service.ts.
import {
  COMMENT_MAX_LENGTH,
  COMMENT_REQUIRED_REASONS,
  INFLOW_REASONS,
  LOT_MAX_LENGTH,
  OUTFLOW_REASONS,
  stockMovementReasonSchema,
} from '@simsm/shared-types';
import type { StockMovementReason } from '@simsm/shared-types';
import type { DraftRow, DraftRowSourceIssue } from '../../../../features/reporting/api';

export const C2_COLUMNS = [
  'stock_date',
  'cod_locatie',
  'cod_medicament',
  'lot',
  'data_expirare_lot',
  'cantitate_curenta',
  'motiv',
  'comentariu',
  'corecteaza_raportul',
] as const;
export type C2Column = (typeof C2_COLUMNS)[number];

/** Columns whose absence makes the file unreadable, not merely a row invalid. */
export const C2_REQUIRED_COLUMNS: readonly C2Column[] = [
  'cod_locatie',
  'cod_medicament',
  'lot',
  'data_expirare_lot',
  'cantitate_curenta',
  'motiv',
];

export const C2_REASONS: readonly StockMovementReason[] = stockMovementReasonSchema.options;

// The draft endpoint caps a row set at 2000 (upsertDraftRequestSchema), and the
// rows already in the draft count against it, so the file is capped at the same
// number and the remainder is checked against what the draft holds.
export const C2_MAX_ROWS = 2000;

// Field widths from stockRowSchema. A value over them is refused here rather
// than by a 400 that would reject the whole PATCH and say nothing per row.
const MAX_LENGTHS: Partial<Record<C2Column, number>> = {
  cod_locatie: 64,
  cod_medicament: 64,
  lot: LOT_MAX_LENGTH,
  comentariu: COMMENT_MAX_LENGTH,
};

export type ImportErrorCode =
  | 'MISSING_COLUMN'
  | 'NO_DATA_ROWS'
  | 'TOO_MANY_ROWS'
  | 'MIXED_SITES'
  | 'SITE_MISMATCH'
  | 'REQUIRED'
  | 'UNKNOWN_REASON'
  | 'INVALID_DATE'
  | 'INVALID_EXPIRY'
  | 'INVALID_QUANTITY'
  | 'NEGATIVE_QUANTITY'
  | 'COMMENT_REQUIRED'
  | 'INVALID_CORRECTION_ID'
  | 'TOO_LONG';

export interface ImportError {
  /** 1-based worksheet line, header included, so it matches what Excel shows. */
  line: number;
  column: C2Column | '';
  code: ImportErrorCode;
  value: string;
  limit?: number;
}

export interface ImportRow {
  line: number;
  siteCode: string;
  /** Ready for PATCH /stock/drafts/:id — the wire shape, nothing extra. */
  draftRow: DraftRow;
  /** The expiry as the file states it and as it is sent: `YYYY-MM` (a month) or `YYYY-MM-DD` (a day). */
  expiry: string;
  /** The sign the reason gives the quantity, for display only. */
  signedQuantity: number | undefined;
  errors: ImportError[];
}

export interface ImportReport {
  /**
   * Errors that stop the whole file: a missing column, no rows, too many rows,
   * two locations, or a location other than the selected one (`withSelectedSite`).
   */
  fileErrors: ImportError[];
  rows: ImportRow[];
  /** The one `cod_locatie` the file names, or '' when it names none or many. */
  siteCode: string;
  totalCount: number;
  validCount: number;
  errorCount: number;
}

/** One worksheet line, already read as text and keyed by its normalized header. */
export interface SheetRow {
  line: number;
  cells: Partial<Record<C2Column, string>>;
}

const EXPIRY_RE = /^\d{4}-\d{2}(-\d{2})?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Header text to a C2 column. AMDM's own file is written with the snake_case
 * names, but a spreadsheet that has been through a person picks up capitals,
 * spaces and diacritics, and refusing it over a capital letter would be a
 * refusal about nothing.
 */
export function normalizeHeader(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[\s-]+/g, '_');
}

export function isC2Column(value: string): value is C2Column {
  return (C2_COLUMNS as readonly string[]).includes(value);
}

/**
 * The expiry as the file states it, sent as it is: `YYYY-MM` is a month and
 * `YYYY-MM-DD` is a day (caiet UC40-05 / UC50-02, docs/shape-ops-lot-expiry-precision.md
 * D6). Core is the one place that completes a month to its last day, and it stores
 * the precision the file gave beside the date, so a month stays a month on every
 * later screen (`09/2027`) and a day stays a day, even on the last day of its month.
 * A month completed here would reach core as a day and lose that.
 */
export function expiryOnWire(value: string): string {
  return value;
}

/**
 * The sign is derived from the reason, never given in the file — AMDM's C2
 * contract and caiet UC50-11. Mirrors `signFor` in draft.service.ts for the
 * preview column; the server is what actually signs the stored row.
 */
export function signedQuantity(reason: StockMovementReason, quantity: number): number {
  if ((INFLOW_REASONS as readonly string[]).includes(reason)) return Math.abs(quantity);
  if ((OUTFLOW_REASONS as readonly string[]).includes(reason)) return -Math.abs(quantity);
  return quantity;
}

function newClientRowId(line: number): string {
  return `imp-${line}-${Math.random().toString(36).slice(2, 8)}`;
}

function readCell(row: SheetRow, column: C2Column): string {
  return (row.cells[column] ?? '').trim();
}

function lengthError(line: number, column: C2Column, value: string): ImportError | null {
  const limit = MAX_LENGTHS[column];
  if (limit === undefined || value.length <= limit) return null;
  return { line, column, code: 'TOO_LONG', value, limit };
}

// The draft field a C2 column fills. `cod_locatie` has none: the rows land on
// the selected location, and a code that differs from it is a file error.
const DRAFT_FIELD: Partial<Record<C2Column, string>> = {
  stock_date: 'stockDate',
  cod_medicament: 'medicineCode',
  lot: 'lot',
  data_expirare_lot: 'lotExpiry',
  cantitate_curenta: 'currentQuantity',
  motiv: 'reason',
  comentariu: 'comment',
  corecteaza_raportul: 'correctionOf',
};

/**
 * The code a source issue carries into the draft (specs/054 R11). Where core
 * has a row code for the same fact it is that code, so the issue reads the way
 * the editor's own error would; otherwise it is the import's own name, which
 * core reports as SOURCE_VALUE_INVALID while the row keeps the name.
 *
 * Null for an EMPTY cell (REQUIRED, COMMENT_REQUIRED): there is no text to keep,
 * and core names a missing value itself.
 */
function sourceIssueCode(error: ImportError, quantityOutOfRange: boolean): string | null {
  switch (error.code) {
    case 'NEGATIVE_QUANTITY':
      return 'NEGATIVE_QUANTITY';
    case 'INVALID_QUANTITY':
      return quantityOutOfRange ? 'QUANTITY_OUT_OF_RANGE' : 'QUANTITY_NON_INTEGER';
    case 'INVALID_EXPIRY':
      return 'EXPIRY_FORMAT';
    case 'UNKNOWN_REASON':
      return 'REASON_UNKNOWN';
    case 'TOO_LONG':
      if (error.column === 'lot') return 'LOT_TOO_LONG';
      return error.column === 'comentariu' ? 'COMMENT_TOO_LONG' : 'TOO_LONG';
    case 'INVALID_CORRECTION_ID':
      return 'CORRECTION_ID_INVALID';
    case 'INVALID_DATE':
      return 'STOCK_DATE_FORMAT';
    default:
      return null;
  }
}

function parseRow(row: SheetRow): ImportRow {
  const errors: ImportError[] = [];
  const push = (column: C2Column, code: ImportErrorCode, value: string): void => {
    errors.push({ line: row.line, column, code, value });
  };

  const siteCode = readCell(row, 'cod_locatie');
  const medicineCode = readCell(row, 'cod_medicament');
  const lot = readCell(row, 'lot');
  const expiryRaw = readCell(row, 'data_expirare_lot');
  const quantityRaw = readCell(row, 'cantitate_curenta');
  const reasonRaw = readCell(row, 'motiv').toUpperCase();
  const comment = readCell(row, 'comentariu');
  const stockDate = readCell(row, 'stock_date');
  const correctionOf = readCell(row, 'corecteaza_raportul');

  for (const [column, value] of [
    ['cod_locatie', siteCode],
    ['cod_medicament', medicineCode],
    ['lot', lot],
    ['comentariu', comment],
  ] as [C2Column, string][]) {
    const tooLong = lengthError(row.line, column, value);
    if (tooLong) errors.push(tooLong);
  }

  if (siteCode === '') push('cod_locatie', 'REQUIRED', '');
  if (medicineCode === '') push('cod_medicament', 'REQUIRED', '');
  if (lot === '') push('lot', 'REQUIRED', '');

  if (expiryRaw === '') push('data_expirare_lot', 'REQUIRED', '');
  else if (!EXPIRY_RE.test(expiryRaw)) push('data_expirare_lot', 'INVALID_EXPIRY', expiryRaw);

  // `stock_date` is optional and defaults to today, which core-service applies
  // at confirm time; an unusable value is refused rather than silently dropped.
  if (stockDate !== '' && !DATE_RE.test(stockDate)) {
    push('stock_date', 'INVALID_DATE', stockDate);
  }

  let quantity: number | undefined;
  let quantityOutOfRange = false;
  if (quantityRaw === '') {
    push('cantitate_curenta', 'REQUIRED', '');
  } else {
    const parsed = Number(quantityRaw.replace(',', '.'));
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
      push('cantitate_curenta', 'INVALID_QUANTITY', quantityRaw);
    } else if (!Number.isSafeInteger(parsed)) {
      // An integer past 2^53 is already a different number once parsed, and the
      // wire would carry that other number. Kept as its text instead.
      quantityOutOfRange = true;
      push('cantitate_curenta', 'INVALID_QUANTITY', quantityRaw);
    } else if (parsed < 0) {
      // The sign belongs to the reason, so a minus in the file is a statement
      // the contract has no room for and the operator has to resolve. It costs
      // one honest consequence: C2 cannot express a downward CORRECTION, which
      // core-service does accept from a caller that signs its own quantity.
      push('cantitate_curenta', 'NEGATIVE_QUANTITY', quantityRaw);
    } else {
      quantity = parsed;
    }
  }

  let reason: StockMovementReason | undefined;
  if (reasonRaw === '') {
    push('motiv', 'REQUIRED', '');
  } else if (!(C2_REASONS as readonly string[]).includes(reasonRaw)) {
    push('motiv', 'UNKNOWN_REASON', reasonRaw);
  } else {
    reason = reasonRaw as StockMovementReason;
    if ((COMMENT_REQUIRED_REASONS as readonly string[]).includes(reason) && comment === '') {
      push('comentariu', 'COMMENT_REQUIRED', reason);
    }
  }

  if (correctionOf !== '' && !UUID_RE.test(correctionOf)) {
    push('corecteaza_raportul', 'INVALID_CORRECTION_ID', correctionOf);
  }

  // Every cell the file got wrong travels as a source issue with the cell's own
  // text (specs/054 R11), and core folds it into the row's validation errors
  // until the operator edits that field. The field itself is left out: the wire
  // must never carry a value it cannot represent (POST /stock/drafts answers a
  // fractional quantity with a 400 for the whole call), and an absent
  // `currentQuantity` is honest since spec 008's presence flag.
  const sourceIssues: DraftRowSourceIssue[] = [];
  const badFields = new Set<string>();
  for (const error of errors) {
    if (error.column === '') continue;
    const field = DRAFT_FIELD[error.column];
    const code = sourceIssueCode(error, quantityOutOfRange);
    if (field === undefined || code === null) continue;
    badFields.add(field);
    sourceIssues.push({ field, code, raw: readCell(row, error.column) });
  }
  const sendable = (field: string, value: string): boolean => value !== '' && !badFields.has(field);

  // Built with the keys the row actually carries: under
  // `exactOptionalPropertyTypes` an optional field does not accept an explicit
  // `undefined`, and an absent key is what "the file did not say" means on the
  // wire. An empty cell is left out too, and core raises its own per-row error
  // against it in the draft editor.
  const draftRow: DraftRow = { clientRowId: newClientRowId(row.line), validationErrors: [] };
  if (sendable('medicineCode', medicineCode)) draftRow.medicineCode = medicineCode;
  if (sendable('lot', lot)) draftRow.lot = lot;
  if (sendable('lotExpiry', expiryRaw)) draftRow.lotExpiry = expiryOnWire(expiryRaw);
  if (quantity !== undefined) draftRow.currentQuantity = quantity;
  if (reason !== undefined) draftRow.reason = reason;
  if (sendable('comment', comment)) draftRow.comment = comment;
  if (sendable('correctionOf', correctionOf)) draftRow.correctionOf = correctionOf;
  if (sourceIssues.length > 0) draftRow.sourceIssues = sourceIssues;

  return {
    line: row.line,
    siteCode,
    draftRow,
    expiry: expiryOnWire(expiryRaw),
    signedQuantity:
      reason !== undefined && quantity !== undefined ? signedQuantity(reason, quantity) : undefined,
    errors,
  };
}

/**
 * The whole check, over a sheet already read as text. Everything a file can get
 * wrong is decided here so the screen can show it before a single row is
 * written, which is the point of the screen (UC50-11).
 */
export function buildImportReport(headers: string[], rows: SheetRow[]): ImportReport {
  const present = new Set(headers);
  const fileErrors: ImportError[] = C2_REQUIRED_COLUMNS.filter(
    (column) => !present.has(column),
  ).map((column) => ({ line: 1, column, code: 'MISSING_COLUMN' as const, value: '' }));

  if (fileErrors.length > 0) {
    return { fileErrors, rows: [], siteCode: '', totalCount: 0, validCount: 0, errorCount: 0 };
  }
  if (rows.length === 0) {
    return {
      fileErrors: [{ line: 1, column: '', code: 'NO_DATA_ROWS', value: '' }],
      rows: [],
      siteCode: '',
      totalCount: 0,
      validCount: 0,
      errorCount: 0,
    };
  }
  if (rows.length > C2_MAX_ROWS) {
    fileErrors.push({
      line: 1,
      column: '',
      code: 'TOO_MANY_ROWS',
      value: String(rows.length),
      limit: C2_MAX_ROWS,
    });
  }

  const parsed = rows.map(parseRow);

  // A draft is bound to ONE site: core-service writes `siteId: draft.siteId`
  // onto every stock row at confirm time and ignores a per-row site code
  // (draft.service.ts, confirmDraft). So a file naming two locations cannot be
  // imported through the draft at all, and saying so up front beats splitting
  // it silently across a location it was not reported for.
  const distinctSites = [...new Set(parsed.map((row) => row.siteCode).filter((c) => c !== ''))];
  if (distinctSites.length > 1) {
    fileErrors.push({
      line: 1,
      column: 'cod_locatie',
      code: 'MIXED_SITES',
      value: distinctSites.join(', '),
    });
  }

  const errorCount = parsed.filter((row) => row.errors.length > 0).length;
  return {
    fileErrors,
    rows: parsed,
    siteCode: distinctSites.length === 1 ? (distinctSites[0] as string) : '',
    totalCount: parsed.length,
    validCount: parsed.length - errorCount,
    errorCount,
  };
}

/**
 * The file's location against the one the operator selected (specs/054 R11):
 * a draft belongs to one location, and a file reported for another must not
 * land on it. Returns a new report with a SITE_MISMATCH file error naming every
 * code that differs, or the same report when none does.
 *
 * `selectedSiteCode` is the selected site's `internalCode` (GET /stock/locations).
 * Compared trimmed and case-blind, as headers are: a refusal over a capital
 * letter would be a refusal about nothing. A blank `cod_locatie` is left to its
 * row error; the row lands on the selected location either way.
 */
export function withSelectedSite(report: ImportReport, selectedSiteCode: string): ImportReport {
  const selected = selectedSiteCode.trim().toLowerCase();
  const differing = [
    ...new Set(
      report.rows
        .map((row) => row.siteCode)
        .filter((code) => code !== '' && code.toLowerCase() !== selected),
    ),
  ];
  if (differing.length === 0) return report;
  return {
    ...report,
    fileErrors: [
      ...report.fileErrors,
      { line: 1, column: 'cod_locatie', code: 'SITE_MISMATCH', value: differing.join(', ') },
    ],
  };
}

/** A file with no file-level error goes into the draft whole, broken rows included. */
export function reportIsImportable(report: ImportReport): boolean {
  return report.fileErrors.length === 0 && report.totalCount > 0;
}

// WHY EVERY ROW IS CARRIED INTO THE DRAFT, broken ones included, as the
// wireframe's `gata` step (wireframes/react/src/pages/Import.tsx) draws it.
//
// Until specs/054 the broken rows were left out. `DraftRow.currentQuantity`
// crossed gRPC as a proto3 int64 with no presence, so a quantity left out
// arrived at core-service as 0: a row whose cell read `-3` landed in the draft
// as `currentQuantity: 0` with no error, ready to be confirmed as a destruction
// of nothing. Spec 008 fixed that blocker end to end with `currentQuantitySet`
// (stock.proto; apps/bff/src/stock/drafts.controller.ts), so an absent quantity
// now reaches core as absent and is refused as QUANTITY_REQUIRED.
//
// What was left was losing the cell's text. So a bad cell now travels as a
// source issue {field, code, raw} with the field left out, core shows it on the
// field until the operator edits it, and the draft cannot be confirmed while it
// stands (R11). The operator fixes the row in the draft, not in the file.

// What a dedicated endpoint would need, if AMDM's C2 file is ever to be posted
// rather than parsed in the browser (the medicine nomenclator already has one —
// POST /admin/medicines/import?dryRun=true — and it is the shape to copy):
//
//   1. `POST /api/v1/stock/import?dryRun=true` on the bff, multipart, behind
//      SessionGuard, reusing the exceljs reader that apps/bff/src/catalog/
//      medicine-import.ts already runs server-side.
//   2. A `cod_locatie` → site resolution done server-side. In the browser the
//      operator selects the location (GET /stock/locations, which carries each
//      site's `internalCode`) and `withSelectedSite` refuses a file naming
//      another; an endpoint would have to make the same check itself.
//   3. Per-row validation returned as data (rowIndex + column + code), so the
//      report the operator reads is produced by the same code that writes.
//   4. A file-level idempotency key, so a retried upload does not double a
//      submission the way a re-import into the draft would.
